import { Persona } from "@/types/campaign";
import { motion, Reorder, useDragControls } from "framer-motion";
import { GripVertical, X } from "lucide-react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

interface PersonaListItemProps {
  persona: Persona;
  index: number;
  onRemove: (index: number) => void;
  onEdit: () => void;
}

type DescriptionNode =
  | { type: "text"; value: string }
  | { type: "highlight"; value: string }
  | { type: "no-padding"; value: string };

function formatList(items: string[]): DescriptionNode[] {
  return items.flatMap((item, i) => {
    const nodes: DescriptionNode[] = [];
    if (i > 0 && i < items.length - 1)
      nodes.push({ type: "no-padding", value: "," });
    if (i > 0 && i === items.length - 1)
      nodes.push({ type: "text", value: "eller" });
    nodes.push({ type: "highlight", value: item });
    return nodes;
  });
}

function buildPersonaDescription(persona: Persona): DescriptionNode[] {
  const nodes: DescriptionNode[] = [];

  if (persona.levels.length) {
    nodes.push(...formatList(persona.levels));
  } else {
    nodes.push({ type: "highlight", value: "Alle roller" });
  }

  if (persona.requirePhone) {
    nodes.push({ type: "text", value: "med" });
    nodes.push({ type: "highlight", value: "telefonnummer" });
  }

  if (persona.groups.length) {
    nodes.push({
      type: "text",
      value: persona.groups.length > 1 ? "i afdelingerne" : "i afdelingen",
    });
    nodes.push(...formatList(persona.groups));
  } else {
    nodes.push({ type: "text", value: "i" });
    nodes.push({ type: "highlight", value: "Alle afdelinger" });
  }

  if (persona.titles.length) {
    nodes.push({
      type: "text",
      value: persona.titles.length > 1 ? "med titlerne" : "med titlen",
    });
    nodes.push(...formatList(persona.titles.map((t) => t.title)));
  }

  return nodes;
}

export function PersonaListItem({
  persona,
  index,
  onRemove,
  onEdit,
}: PersonaListItemProps) {
  const nodes = buildPersonaDescription(persona);
  const controls = useDragControls();
  const [isDragging, setIsDragging] = useState(false);

  return (
    <Reorder.Item
      value={persona}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
    >
      <motion.div
        layout
        className={
          "group dragging:opacity-50 relative flex min-h-12.5 items-center gap-3 select-none"
        }
      >
        <div
          className="text-theme-silver-gradient-from reorder-handle shrink-0 cursor-grab touch-none active:cursor-grabbing"
          onPointerDown={(e) => controls.start(e)}
        >
          <GripVertical className="h-5 w-5 opacity-60 transition-opacity group-hover:opacity-100" />
        </div>
        <div
          className={twMerge(
            "border-theme-outline-variant flex flex-1 cursor-pointer items-center gap-3 overflow-hidden rounded-lg border p-2 px-4 transition-colors",
            isDragging && "backdrop-blur-sm transition-all",
          )}
        >
          <div className="dragging:pointer-events-none flex w-full items-center gap-2">
            <div
              className="min max-w-158 min-w-0 flex-1 pr-6"
              onClick={() => onEdit()}
            >
              <p className="leading-8 break-all">
                {nodes.map((node, i) => (
                  <span key={i}>
                    {i > 0 && node.type !== "no-padding" && " "}
                    <span
                      className={
                        node.type === "highlight"
                          ? "text-xs font-bold"
                          : "text-theme-on-glass-variant text-xs font-normal"
                      }
                    >
                      {node.value}
                    </span>
                  </span>
                ))}
              </p>
            </div>
            <X
              onClick={() => onRemove(index)}
              className="text-theme-on-glass-variant absolute top-6.5 right-4 h-4 w-4 shrink-0 -translate-y-1/2 cursor-pointer"
            />
          </div>
        </div>
      </motion.div>
    </Reorder.Item>
  );
}
